import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';

// Note: We import from '../lib/utils/cookie-parser' but this will only be used
// client-side, so we need to make sure it's compatible with browser environment
const parseInfoCustIdpCookie = typeof window !== 'undefined'
  ? require('../lib/utils/cookie-parser').parseInfoCustIdpCookie
  : () => null;

// Import isSecureServerDomain to check if we're on secureserver.net
// Cookie loading is only active on custom domains
const isSecureServerDomain = typeof window !== 'undefined'
  ? require('../lib/utils/cookie-sync').isSecureServerDomain
  : () => false;

// Auth types that involve delegation/impersonation - PLID from props should be preserved
// e2s = employee-to-shopper (CRM agent impersonating customer)
// e2s2s = employee-to-shopper-to-shopper (nested delegation)
// s2s = server-to-server (API calls)
const DELEGATION_AUTH_TYPES = ['e2s', 'e2s2s', 's2s'];

/**
 * Custom hook to monitor cookie changes
 * Polls the info_idp cookie and returns the parsed customer data
 * Re-parses when the cookie value changes
 * Note: Only active on custom domains, skips on secureserver.net
 *
 * @param {Object} initialCustomer - Initial customer data from SSR
 * @param {number} pollInterval - Polling interval in ms (default: 1000ms)
 * @returns {Object|null} Parsed customer data from cookie
 */
function useCustomerFromCookie(initialCustomer = null, pollInterval = 1000) {
  const [cookieCustomer, setCookieCustomer] = useState(initialCustomer);
  const lastCookieValue = useRef('');
  const timeoutRef = useRef(null);

  const checkCookie = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    // Skip cookie loading on secureserver.net domains
    // Customer data on these domains comes from withManifest HOC
    if (isSecureServerDomain()) {
      return;
    }

    // Get current cookie value
    const cookies = document.cookie.split(';');
    const infoCookie = cookies.find(c => c.trim().startsWith('info_idp='));
    const currentCookieValue = infoCookie ? infoCookie.split('=')[1] : '';

    // Only re-parse if cookie value has changed
    if (currentCookieValue !== lastCookieValue.current) {
      lastCookieValue.current = currentCookieValue;

      if (currentCookieValue) {
        const parsed = parseInfoCustIdpCookie();
        if (parsed) {
          setCookieCustomer(parsed);
        }
      } else {
        // Cookie was removed
        setCookieCustomer(null);
      }
    }
  }, []);

  useEffect(() => {
    // Skip on server-side
    if (typeof window === 'undefined') {
      return;
    }

    // Skip polling on secureserver.net domains
    // Customer data comes from withManifest HOC on these domains
    if (isSecureServerDomain()) {
      return;
    }

    // Initial check
    checkCookie();

    // Set up polling to detect cookie changes
    const poll = () => {
      checkCookie();
      timeoutRef.current = setTimeout(poll, pollInterval);
    };

    timeoutRef.current = setTimeout(poll, pollInterval);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [checkCookie, pollInterval]);

  return cookieCustomer;
}

/**
 * HOC that injects customer data from info_idp cookie
 * This wrapper reads the cookie both server-side (via props) and client-side (via document.cookie)
 * It monitors cookie changes and updates the component when the cookie is synced
 *
 * Note: For delegation auth types (e2s, e2s2s, s2s), we preserve the privateLabelId from props
 * to ensure the correct reseller branding is shown when CRM agents impersonate customers.
 *
 * @param {React.Component} WrappedComponent - Component to wrap
 * @returns {React.Component} Enhanced component with customer data from cookie
 */
export function withCustomerFromCookie(WrappedComponent) {
  const CustomerFromCookieWrapper = function CustomerFromCookieWrapper(props) {
    // Use custom hook to monitor cookie changes
    const cookieCustomer = useCustomerFromCookie(props.customerFromCookie);

    // Merge cookie customer with existing customer prop
    // Cookie data takes precedence for authentication state
    let customer = cookieCustomer ? {
      ...props.customer,
      ...cookieCustomer,
      // Override items count - keep cart items if available
      items: props.customer?.items || cookieCustomer.items || 0
    } : props.customer;

    // For delegation auth types (e2s, e2s2s, s2s), preserve privateLabelId from props
    // This ensures correct reseller branding when CRM agents impersonate customers
    // The cookie may contain PLID=1 (GoDaddy) but the actual reseller PLID should be used
    if (cookieCustomer &&
        DELEGATION_AUTH_TYPES.includes(cookieCustomer.authType) &&
        props.privateLabelId) {
      customer = {
        ...customer,
        privateLabelId: props.privateLabelId
      };
    }

    return <WrappedComponent { ...props } customer={ customer } />;
  };

  CustomerFromCookieWrapper.propTypes = {
    customerFromCookie: PropTypes.object,
    customer: PropTypes.shape({
      items: PropTypes.number
    })
  };

  // Set displayName for better debugging in React DevTools
  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  CustomerFromCookieWrapper.displayName = `withCustomerFromCookie(${wrappedComponentName})`;

  return CustomerFromCookieWrapper;
}

export default withCustomerFromCookie;
